import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  useTheme,
  CircularProgress,
  Chip,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Paper,
  useMediaQuery,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import PageSalesCta from '../components/PageSalesCta';
import { motion } from 'framer-motion';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SchoolIcon from '@mui/icons-material/School';
import PublicIcon from '@mui/icons-material/Public';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import LinkIcon from '@mui/icons-material/Link';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupsIcon from '@mui/icons-material/Groups';
import FilterListIcon from '@mui/icons-material/FilterList';


const CourseSingle = () => {
  const { slug } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyData, setApplyData] = useState({ name: '', email: '', phone: '' });
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyMessage, setApplyMessage] = useState(null);
  const [contentOpen, setContentOpen] = useState(false);
  const [contentList, setContentList] = useState([]);
  const [contentLoading, setContentLoading] = useState(false);
  const [checkEmail, setCheckEmail] = useState('');

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const res = await apiService.getCourseBySlug(slug);
        if (res.success && res.course) setCourse(res.course);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    if (slug) fetchCourse();
  }, [slug]);

  const handleApply = async () => {
    if (!course || !applyData.name.trim() || !applyData.email.trim() || !applyData.phone.trim()) return;
    setApplyLoading(true);
    setApplyMessage(null);
    try {
      const res = await apiService.applyCourse(course.id, applyData);
      setApplyMessage({ type: 'success', text: res.message });
      setApplyOpen(false);
      setApplyData({ name: '', email: '', phone: '' });
    } catch (err) {
      setApplyMessage({ type: 'error', text: err.response?.data?.message || 'Application failed.' });
    } finally {
      setApplyLoading(false);
    }
  };

  const handleCheckAccess = async () => {
    if (!course || !checkEmail.trim()) return;
    setContentLoading(true);
    try {
      const res = await apiService.checkEnrollment(course.id, checkEmail.trim());
      if (res.has_access) {
        const contentRes = await apiService.getCourseContent(course.id, checkEmail.trim());
        setContentList(contentRes.content || []);
      } else {
        setApplyMessage({ type: 'info', text: 'No access yet. Apply for the course or complete payment.' });
      }
    } catch {
      setApplyMessage({ type: 'error', text: 'Could not check access.' });
    } finally {
      setContentLoading(false);
    }
  };

  const openContentDialog = () => {
    setContentList([]);
    setCheckEmail('');
    setContentOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '70vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress size={48} sx={{ color: 'primary.main' }} />
      </Box>
    );
  }
  if (!course) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 2 }}>
        <Typography variant="h5" color="text.secondary" gutterBottom>Course not found</Typography>
        <Button component={RouterLink} to="/courses" startIcon={<ArrowBackIosNewIcon />} sx={{ mt: 2 }}>Back to Courses</Button>
      </Box>
    );
  }

  const isFree = (course.price_type || 'free') === 'free';
  const isOnline = (course.delivery_type || 'online') === 'online';
  const showPrice = !isFree && Number(course.price) > 0;
  const DeliveryIcon = isOnline ? PublicIcon : SchoolIcon;
  const chipIconSx = { color: 'white' };

  return (
    <Box sx={{ minHeight: '100vh', pb: 8, background: theme.palette.mode === 'dark' ? '#0d1117' : '#fafbfc' }}>
      {/* Hero: image full-bleed from top, overlay content cleared by header */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          mb: 4,
          boxShadow: theme.shadows[4],
        }}
      >
        {course.featured_image ? (
          <Box
            component="img"
            src={course.featured_image}
            alt=""
            sx={{
              width: '100%',
              height: { xs: 220, sm: 280, md: 340 },
              objectFit: 'cover',
              display: 'block',
            }}
          />
        ) : (
          <Box sx={{ width: '100%', height: { xs: 220, sm: 280, md: 340 }, bgcolor: 'primary.dark', opacity: 0.4 }} />
        )}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, transparent 40%, rgba(0,0,0,0.85) 100%)',
            color: 'white',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            pt: 2,
            px: 2,
            pb: 3,
          }}
        >
          <Container maxWidth="md">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
              <Chip size="small" label={isFree ? 'Free' : 'Paid'} sx={{ bgcolor: isFree ? '#10B981' : 'primary.main', color: 'white', fontWeight: 600 }} />
              <Chip size="small" icon={<DeliveryIcon sx={chipIconSx} />} label={isOnline ? 'Online' : 'In-person'} sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white' }} />
              {course.duration ? <Chip size="small" icon={<ScheduleIcon sx={chipIconSx} />} label={course.duration} sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white' }} /> : null}
              {showPrice ? <Chip size="small" label={'KES ' + Number(course.price).toLocaleString()} sx={{ bgcolor: 'secondary.main', color: 'white', fontWeight: 600 }} /> : null}
            </Box>
            <Typography component="h1" variant="h4" sx={{ fontWeight: 700, color: 'white', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
              {course.title}
            </Typography>
          </Container>
        </Box>
      </Box>

      <Container maxWidth="md">
        {course.excerpt && (
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
            {course.excerpt}
          </Typography>
        )}

        <Paper elevation={0} sx={{ p: 3, borderRadius: 2, mb: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ '& p': { mb: 2 }, '& img': { maxWidth: '100%', borderRadius: 2 } }} dangerouslySetInnerHTML={{ __html: course.description || '' }} />
        </Paper>

        {applyMessage && (
          <Alert severity={applyMessage.type} sx={{ mb: 3 }} onClose={() => setApplyMessage(null)}>
            {applyMessage.text}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            onClick={() => setApplyOpen(true)}
            startIcon={<CheckCircleOutlineIcon />}
            sx={{ px: 3, py: 1.5, borderRadius: 2 }}
          >
            Apply for this course
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={openContentDialog}
            startIcon={<PlayCircleOutlineIcon />}
            sx={{ px: 3, py: 1.5, borderRadius: 2 }}
          >
            I have access — view content
          </Button>
          <Button component={RouterLink} to="/courses" startIcon={<ArrowBackIosNewIcon />} sx={{ color: 'text.secondary' }}>
            Back to Courses
          </Button>
        </Box>
      </Container>
      <PageSalesCta />

      {/* Apply Dialog – positioned below header, modern form */}
      <Dialog
        open={applyOpen}
        onClose={() => setApplyOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            mt: isMobile ? 0 : 6,
            mx: isMobile ? 0 : 2,
            borderRadius: isMobile ? 0 : 3,
            overflow: 'hidden',
            boxShadow: 24,
          },
        }}
      >
        <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Apply for this course</Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>{course.title}</Typography>
        </Box>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <TextField fullWidth label="Full name" value={applyData.name} onChange={(e) => setApplyData((d) => ({ ...d, name: e.target.value }))} margin="normal" required />
          <TextField fullWidth label="Email" type="email" value={applyData.email} onChange={(e) => setApplyData((d) => ({ ...d, email: e.target.value }))} margin="normal" required />
          <TextField fullWidth label="Phone number" value={applyData.phone} onChange={(e) => setApplyData((d) => ({ ...d, phone: e.target.value }))} margin="normal" required />
          {!isFree && (
            <Alert severity="info" sx={{ mt: 2 }} icon={false}>
              This is a paid course. After applying we will send you M-Pesa payment details. Once payment is confirmed you will get access to the content.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
          <Button onClick={() => setApplyOpen(false)}>Cancel</Button>
          <Button variant="contained" color="secondary" onClick={handleApply} disabled={applyLoading}>
            {applyLoading ? <CircularProgress size={24} /> : 'Submit application'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Content / Check access Dialog */}
      <Dialog
        open={contentOpen}
        onClose={() => { setContentOpen(false); setContentList([]); setCheckEmail(''); }}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            mt: isMobile ? 0 : 6,
            mx: isMobile ? 0 : 2,
            borderRadius: isMobile ? 0 : 3,
            overflow: 'hidden',
            boxShadow: 24,
          },
        }}
      >
        {contentList.length > 0 ? (
          <>
            <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Course content</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>{course.title}</Typography>
            </Box>
            <DialogContent sx={{ pt: 3 }}>
              {contentList.map((item, i) => (
                <Paper key={i} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                    {item.type === 'video' ? 'Video' : 'Resource'}
                  </Typography>
                  <Button
                    fullWidth
                    component="a"
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={item.type === 'video' ? <VideoLibraryIcon /> : <LinkIcon />}
                    sx={{ justifyContent: 'flex-start', textAlign: 'left', mt: 0.5 }}
                  >
                    {item.title || item.url}
                  </Button>
                </Paper>
              ))}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button onClick={() => { setContentOpen(false); setContentList([]); setCheckEmail(''); }}>Close</Button>
            </DialogActions>
          </>
        ) : (
          <>
            <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>View course content</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>Enter the email you used when applying</Typography>
            </Box>
            <DialogContent sx={{ pt: 3 }}>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                We will check if you have access (after approval or payment confirmation).
              </Typography>
              <TextField fullWidth label="Your email" type="email" value={checkEmail} onChange={(e) => setCheckEmail(e.target.value)} margin="normal" />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button onClick={() => { setContentOpen(false); setCheckEmail(''); }}>Cancel</Button>
              <Button variant="contained" onClick={handleCheckAccess} disabled={contentLoading || !checkEmail.trim()}>
                {contentLoading ? <CircularProgress size={24} /> : 'Check access'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

const WHY_APPLY = [
  {
    icon: WorkspacePremiumIcon,
    title: 'Industry-relevant skills',
    text: 'Learn what employers need. Our courses are designed with real-world projects and up-to-date tools.',
  },
  {
    icon: TrendingUpIcon,
    title: 'Boost your career',
    text: 'Free and paid options to fit your budget. Get certified and stand out in the job market.',
  },
  {
    icon: GroupsIcon,
    title: 'Support when you need it',
    text: 'Access course content after approval or payment. We’re here to help you succeed.',
  },
];

const CoursesList = () => {
  const theme = useTheme();
  const [data, setData] = useState({ courses: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'free' | 'paid'

  useEffect(() => {
    apiService.getCourses().then(setData).finally(() => setLoading(false));
  }, []);

  const courses = data.courses || [];
  const filtered =
    filter === 'all'
      ? courses
      : filter === 'free'
        ? courses.filter((c) => (c.price_type || 'free') === 'free')
        : courses.filter((c) => (c.price_type || 'free') === 'paid');

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress size={48} sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', pb: 8, background: theme.palette.mode === 'dark' ? '#0d1117' : '#fafbfc' }}>
      {/* Hero: full-bleed from top, padding clears header so no gap */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1D1D4E 0%, #2D2D6E 50%, #1a1a45 100%)',
          color: 'white',
          pt: { xs: 2, sm: 3 },
          pb: { xs: 4, sm: 5 },
          px: 2,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: '-0.02em', mb: 1.5 }}>
            Our Courses
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, maxWidth: 560, mx: 'auto', lineHeight: 1.6 }}>
            Build in-demand skills with free and paid courses. Apply once — get access to materials and support.
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.85, mt: 2, maxWidth: 520, mx: 'auto' }}>
            Need custom training for your team or organisation?{' '}
            <Box component={RouterLink} to="/contact" sx={{ color: 'secondary.light', fontWeight: 700, textDecoration: 'underline' }}>
              Contact us for a corporate package
            </Box>
            .
          </Typography>
        </Container>
      </Box>

      {/* Why apply — sleek strip */}
      <Box
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: { xs: 3, md: 4 },
          px: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="subtitle2" sx={{ textAlign: 'center', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1.2, mb: 2 }}>
            Why apply for our courses?
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {WHY_APPLY.map((item, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <Box sx={{ textAlign: 'center', maxWidth: 320, mx: 'auto' }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      bgcolor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 1.5,
                    }}
                  >
                    <item.icon sx={{ fontSize: 28 }} />
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {item.text}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Filter + course grid */}
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon color="action" fontSize="small" />
            <Typography variant="body2" color="text.secondary">
              Show:
            </Typography>
          </Box>
          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={(_, v) => v != null && setFilter(v)}
            size="small"
            sx={{
              '& .MuiToggleButtonGroup-grouped': { textTransform: 'none', fontWeight: 600 },
            }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="free">Free</ToggleButton>
            <ToggleButton value="paid">Paid</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            {filtered.length} course{filtered.length !== 1 ? 's' : ''}
          </Typography>
        </Box>

        {courses.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
            <Typography color="text.secondary">No courses available yet. Check back soon.</Typography>
          </Paper>
        ) : filtered.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
            <Typography color="text.secondary">
              No {filter === 'free' ? 'free' : 'paid'} courses at the moment. Try &quot;All&quot; to see everything.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {filtered.map((c, i) => {
              const isFree = (c.price_type || 'free') === 'free';
              const isOnline = (c.delivery_type || 'online') === 'online';
              const showPrice = !isFree && Number(c.price) > 0;
              return (
                <Grid item xs={12} sm={6} md={4} key={c.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: i * 0.05 }}
                  >
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 32px rgba(29, 29, 78, 0.12)',
                          borderColor: 'primary.light',
                        },
                      }}
                    >
                      <CardActionArea component={RouterLink} to={`/courses/${c.slug}`} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                        <Box sx={{ position: 'relative' }}>
                          {c.featured_image ? (
                            <Box component="img" src={c.featured_image} alt="" sx={{ height: 180, width: '100%', objectFit: 'cover' }} />
                          ) : (
                            <Box sx={{ height: 180, bgcolor: 'primary.main', opacity: 0.12 }} />
                          )}
                          <Box sx={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip size="small" label={isFree ? 'Free' : 'Paid'} sx={{ bgcolor: isFree ? '#10B981' : 'primary.main', color: 'white', fontWeight: 600 }} />
                            <Chip size="small" label={isOnline ? 'Online' : 'In-person'} sx={{ bgcolor: 'rgba(255,255,255,0.95)', color: 'text.primary', fontWeight: 500 }} />
                          </Box>
                        </Box>
                        <CardContent sx={{ flex: 1, p: 2.5 }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, lineHeight: 1.3, fontSize: '1.05rem' }}>
                            {c.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.5 }}>
                            {c.excerpt || 'No description'}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            {c.duration && <Chip size="small" icon={<ScheduleIcon sx={{ fontSize: 16 }} />} label={c.duration} variant="outlined" sx={{ fontSize: '0.75rem' }} />}
                            {showPrice ? <Chip size="small" label={`KES ${Number(c.price).toLocaleString()}`} color="primary" sx={{ fontWeight: 600 }} /> : null}
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>
      <PageSalesCta />
    </Box>
  );
};

const Courses = () => {
  const { slug } = useParams();
  if (slug) return <CourseSingle />;
  return <CoursesList />;
};

export default Courses;
